import { z } from 'zod';
import { moneySchema } from '../money';
import { listQuerySchema } from '../common/pagination';


/** Mengeneinheit / Make-or-Buy (Modul inventory, ADR 0006). */
export const articleUnitSchema = z.enum([
  'PIECE',
  'METER',
  'KILOGRAM',
  'LITER',
  'SQUARE_METER',
  'HOUR',
]);
export type ArticleUnit = z.infer<typeof articleUnitSchema>;

/**
 * Kanonische, lesbare Einheiten-Labels (Single Source of Truth für Frontend UND
 * PDF/Backend). Die Roh-Enum-Werte („PIECE", „METER" …) dürfen NIE in der UI oder
 * auf Belegen erscheinen — immer `formatUnit()` verwenden.
 */
export const UNIT_LABELS_DE: Record<ArticleUnit, string> = {
  PIECE: 'Stk',
  METER: 'm',
  KILOGRAM: 'kg',
  LITER: 'l',
  SQUARE_METER: 'm²',
  HOUR: 'Std',
};

export const UNIT_LABELS_EN: Record<ArticleUnit, string> = {
  PIECE: 'pcs',
  METER: 'm',
  KILOGRAM: 'kg',
  LITER: 'l',
  SQUARE_METER: 'm²',
  HOUR: 'h',
};

/**
 * Einheit lesbar darstellen. Tolerant: bekannte Enum-Werte werden übersetzt,
 * beliebiger Freitext (z. B. importierte oder eigene Einheiten wie „Paar") und
 * bereits übersetzte Labels bleiben unverändert. `null`/leer → ''.
 */
export function formatUnit(unit: string | null | undefined, lang: 'de' | 'en' = 'de'): string {
  if (!unit) return '';
  const map = lang === 'en' ? UNIT_LABELS_EN : UNIT_LABELS_DE;
  return (map as Record<string, string>)[unit] ?? unit;
}

export const sourcingSchema = z.enum(['MAKE', 'BUY']);
export type Sourcing = z.infer<typeof sourcingSchema>;

/**
 * Beschaffungsart eines Artikels (reiner Zukauf, ADR 0009):
 * - `PROJEKTBEZOGEN`: pro Projekt zukaufen — Bedarf führt zur Bestellung, kein Lagerbestand/Meldebestand.
 * - `STANDARD`: Lagerartikel — immer am Lager, bei Unterschreitung des Mindestbestands nachbestellen
 *   (Ampel/Meldebestand relevant).
 */
export const articleTypeSchema = z.enum(['PROJEKTBEZOGEN', 'STANDARD']);
export type ArticleType = z.infer<typeof articleTypeSchema>;

/** Warengruppe: 4-stellige Zahl (ADR 0016). Hauptwarengruppe = Tausenderstelle. */
export const commodityGroupSchema = z.number().int().min(1000).max(9999);

/** Hauptwarengruppe eines 4-stelligen Warengruppen-Codes (1100 → 1000, 3340 → 3000). */
export function mainGroupOf(commodityGroup: number): number {
  return Math.floor(commodityGroup / 1000) * 1000;
}

/**
 * Bestandslage als ein Filter statt mehrerer sich widersprechender Häkchen
 * (2026-08-11): „unter Mindestbestand", „ohne Bestand" und „über Höchstbestand"
 * schließen einander aus — als Auswahlliste kann man sie gar nicht erst
 * gleichzeitig setzen.
 */
export const articleStockStatusSchema = z.enum(['BELOW_MIN', 'OUT_OF_STOCK', 'IN_STOCK', 'ABOVE_MAX']);
export type ArticleStockStatus = z.infer<typeof articleStockStatusSchema>;

export const ARTICLE_STOCK_STATUS_LABELS: Record<ArticleStockStatus, string> = {
  BELOW_MIN: 'unter Mindestbestand',
  OUT_OF_STOCK: 'ohne Bestand',
  IN_STOCK: 'auf Lager',
  ABOVE_MAX: 'über Höchstbestand',
};

/**
 * Ja/Nein-Merkmale werden als Auswahl mit drei Zuständen gefiltert („egal" =
 * Parameter fehlt). Ein Häkchen kann „nein" nicht ausdrücken — „zeig mir alles
 * OHNE Zeichnung" wäre damit unmöglich.
 */
export const articleFlagFilterSchema = z.enum(['true', 'false']);
export type ArticleFlagFilter = z.infer<typeof articleFlagFilterSchema>;

/** Listen-Query für Artikel: Pagination + Filter (Beschaffung/Mindestbestand/aktiv). */
export const articleListQuerySchema = listQuerySchema.extend({
  sourcing: z.enum(['MAKE', 'BUY']).optional(),
  belowMinStock: z.coerce.boolean().optional(),
  active: z.coerce.boolean().optional(),
  /** Nur Varianten (Ablängungen) dieses Basisartikels listen (ADR 0040). */
  parentArticleId: z.string().uuid().optional(),
  /** Varianten in der Standardliste mitführen (Default: ausgeblendet, nur unterm Basisartikel). */
  includeVariants: z.coerce.boolean().optional(),

  // --- Erweiterte Filter des Artikelstamms (2026-08-11) -------------------
  /** Beschaffungsart (Lagerartikel vs. projektbezogen). */
  articleType: articleTypeSchema.optional(),
  unit: articleUnitSchema.optional(),
  /** Bestandslage; ersetzt `belowMinStock` in der UI, der alte Parameter bleibt gültig. */
  stockStatus: articleStockStatusSchema.optional(),
  /** Kategorie-Mehrfachauswahl, kommagetrennt (Werte kommen aus den Facetten). */
  categories: z.string().trim().max(2000).optional(),
  /** Standardlieferant-Mehrfachauswahl (Namens-Snapshot), kommagetrennt. */
  suppliers: z.string().trim().max(2000).optional(),
  manufacturer: z.string().trim().max(200).optional(),
  countryOfOrigin: z.string().trim().max(120).optional(),
  /** Hauptwarengruppe (4-stellig, ADR 0016) — trifft die ganze Tausendergruppe. */
  commodityMainGroup: z.coerce.number().int().min(0).max(1_000_000).optional(),
  isBlocked: articleFlagFilterSchema.optional(),
  isBillOfMaterials: articleFlagFilterSchema.optional(),
  isCuttable: articleFlagFilterSchema.optional(),
  /** VK-Preisbereich in ganzen Währungseinheiten (Euro), nicht in Mikro-Einheiten. */
  salesPriceMin: z.coerce.number().min(0).optional(),
  salesPriceMax: z.coerce.number().min(0).optional(),
  stockMin: z.coerce.number().optional(),
  stockMax: z.coerce.number().optional(),
});
export type ArticleListQuery = z.infer<typeof articleListQuerySchema>;

/** Ein Filter-Facettenwert mit Trefferanzahl (z. B. Kategorie „Rohre" · 42). */
export const articleFacetSchema = z.object({ value: z.string(), count: z.number().int() });
export type ArticleFacet = z.infer<typeof articleFacetSchema>;

/**
 * Verfügbare Filterwerte des Artikelstamms, mit Anzahl. Damit stehen in den
 * Auswahllisten nur Werte, die es auch wirklich gibt — statt Freitextfelder, in
 * die man sich vertippt und dann null Treffer bekommt.
 */
export const articleFacetsSchema = z.object({
  categories: z.array(articleFacetSchema),
  suppliers: z.array(articleFacetSchema),
  manufacturers: z.array(articleFacetSchema),
  countries: z.array(articleFacetSchema),
});
export type ArticleFacets = z.infer<typeof articleFacetsSchema>;

export const createArticleSchema = z.object({
  number: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9._\-/]+$/, 'Nur Buchstaben, Ziffern, . _ - /'),
  name: z.string().trim().min(1).max(200),
  /** Englischer Name — genutzt für Belege mit Sprache EN (Zweisprachigkeit). */
  nameEn: z.string().trim().max(200).optional(),
  /** Infotext DE (extern, z. B. für Belege/Dokumentation). */
  description: z.string().trim().max(2000).optional(),
  /** Infotext EN. */
  descriptionEn: z.string().trim().max(2000).optional(),
  /** Interne Notizen — erscheinen nie auf Belegen. */
  internalNotes: z.string().trim().max(4000).optional(),
  /** Zolltarifnummer (TARIC), Ziffern mit optionalen Leerzeichen. */
  taricCode: z
    .string()
    .trim()
    .max(20)
    .regex(/^[0-9 ]*$/, 'Nur Ziffern und Leerzeichen.')
    .optional(),
  /** Gewicht je Einheit in Kilogramm. */
  weightKg: z.number().min(0).max(100000).optional(),
  ean: z.string().trim().max(18).optional(),
  /**
   * Alte/fremde Artikelkennung („Artikelnummer alt", „Referenz") — die Nummer,
   * unter der der Artikel früher lief bzw. beim Lieferanten läuft. Kommt aus dem
   * Altdaten-Import (`alt_code`), ist seit 2026-07-31 auch pflegbar und wird von
   * der Artikelsuche mitdurchsucht. Bewusst KEINE Formatprüfung: Altbestand
   * enthält alles von „knh82" bis „GS.R.PU.1".
   */
  altCode: z.string().trim().max(100).optional(),
  /** Kurzname/Suchbegriff aus dem Altsystem — ebenfalls suchbar. */
  matchcode: z.string().trim().max(100).optional(),
  /** Ursprungsland als ISO-3166-1 alpha-2 (z. B. AT, DE, CN). */
  countryOfOrigin: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'ISO-Code mit 2 Buchstaben.')
    .optional(),
  manufacturer: z.string().trim().max(200).optional(),
  /** Standardlieferant (Soft-Ref CRM-Customer SUPPLIER) — id + Name-Snapshot.
   *  Nullable, damit ein gesetzter Lieferant wieder entfernt werden kann („kein Standardlieferant"). */
  defaultSupplierId: z.string().uuid().nullable().optional(),
  defaultSupplierName: z.string().trim().max(200).nullable().optional(),
  unit: articleUnitSchema.default('PIECE'),
  sourcing: sourcingSchema.default('BUY'),
  /** Beschaffungsart: PROJEKTBEZOGEN (pro Projekt zukaufen) vs. STANDARD (Lagerartikel, Meldebestand relevant). */
  articleType: articleTypeSchema.default('STANDARD'),
  /** Warengruppe (4-stellig); Basis fürs Material-Controlling nach Hauptwarengruppe. */
  commodityGroup: commodityGroupSchema.optional(),
  purchasePrice: moneySchema.optional(),
  salesPrice: moneySchema.optional(),
  /** VK-Staffel (ADR 0009): Standard-VK-Menge + bis zu 5 weitere Preisstufen (Preis + Menge-ab). Währung = salesPrice. */
  salesPriceQty: z.number().min(0).optional(),
  salesPrice2: moneySchema.optional(),
  salesPrice2Qty: z.number().min(0).optional(),
  salesPrice3: moneySchema.optional(),
  salesPrice3Qty: z.number().min(0).optional(),
  salesPrice4: moneySchema.optional(),
  salesPrice4Qty: z.number().min(0).optional(),
  salesPrice5: moneySchema.optional(),
  salesPrice5Qty: z.number().min(0).optional(),
  salesPrice6: moneySchema.optional(),
  salesPrice6Qty: z.number().min(0).optional(),
  minStock: z.number().min(0).optional(),
  /** Anfangsbestand (Eröffnungssaldo) — nur beim Anlegen. */
  initialStock: z.number().min(0).default(0),
  /** Freie Schlagworte (z. B. „Standardartikel", „Projektbezogen") für Beschaffungs-Vorschläge. */
  tags: z.array(z.string().trim().min(1).max(40)).max(30).optional(),
  /** Artikel-Kit: Artikel, der aus mehreren Artikeln besteht (Stückliste). Blendet das Kit-Register ein. */
  isBillOfMaterials: z.boolean().optional(),
  /** Ablängbarer Basisartikel (ADR 0040): erlaubt kundenspezifische Längen (Positions-Parameter + Varianten). */
  isCuttable: z.boolean().optional(),
  /** VK je Grundeinheit (z. B. pro Meter) als Basis der Längen-Preisberechnung. */
  cutUnitPrice: moneySchema.optional(),
  /** Feste Abläng-/Rüstpauschale je Stück (optional, zusätzlich zum Längen-Preis). */
  cutSurcharge: moneySchema.optional(),
  isActive: z.boolean().default(true),
});
export type CreateArticleInput = z.infer<typeof createArticleSchema>;

// Die Artikelnummer ist nach dem Anlegen unveränderlich: an ihr hängen als
// Snapshot tausende Positionen/Belege (Soft-Ref + Namensschnappschuss). Daher
// nicht Teil des Update-Schemas (Änderung nur über kontrollierte Varianten-Wege).
export const updateArticleSchema = createArticleSchema.omit({ initialStock: true, number: true }).partial();
export type UpdateArticleInput = z.infer<typeof updateArticleSchema>;

export const articleResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  number: z.string(),
  name: z.string(),
  nameEn: z.string().nullable(),
  description: z.string().nullable(),
  descriptionEn: z.string().nullable(),
  internalNotes: z.string().nullable(),
  taricCode: z.string().nullable(),
  weightKg: z.number().nullable(),
  ean: z.string().nullable(),
  countryOfOrigin: z.string().nullable(),
  manufacturer: z.string().nullable(),
  defaultSupplierId: z.string().uuid().nullable(),
  defaultSupplierName: z.string().nullable(),
  unit: articleUnitSchema,
  sourcing: sourcingSchema,
  articleType: articleTypeSchema,
  category: z.string().nullable(),
  commodityGroup: commodityGroupSchema.nullable(),
  purchasePrice: moneySchema.nullable(),
  /** Ø-EK (durchschnittlicher Einkaufspreis) aus dem Altsystem. */
  avgPurchasePrice: moneySchema.nullable(),
  /** Letzter Bezugspreis aus Einkaufsbelegen — Fallback-EK, wenn kein purchasePrice gepflegt. */
  lastPurchasePrice: moneySchema.nullable(),
  salesPrice: moneySchema.nullable(),
  /** Staffelmenge, ab der der Standard-VK gilt. */
  salesPriceQty: z.number().nullable(),
  /** Weitere VK-Preisstufen (vk_preis2…6) + zugehörige Staffelmengen (Altsystem). */
  salesPrice2: moneySchema.nullable(),
  salesPrice2Qty: z.number().nullable(),
  salesPrice3: moneySchema.nullable(),
  salesPrice3Qty: z.number().nullable(),
  salesPrice4: moneySchema.nullable(),
  salesPrice4Qty: z.number().nullable(),
  salesPrice5: moneySchema.nullable(),
  salesPrice5Qty: z.number().nullable(),
  salesPrice6: moneySchema.nullable(),
  salesPrice6Qty: z.number().nullable(),
  minStock: z.number().nullable(),
  maxStock: z.number().nullable(),
  stockQty: z.number(),
  /** Frei verfügbar = Bestand − aktive Reservierungen (für Lagerstands-Ampel/Meldebestand). */
  available: z.number(),
  /** Aktive Reservierungen (Bestand − reserviert = frei verfügbar). */
  reserved: z.number(),
  matchcode: z.string().nullable(),
  altCode: z.string().nullable(),
  lastSaleDate: z.string().datetime().nullable(),
  isBlocked: z.boolean(),
  isBillOfMaterials: z.boolean(),
  /** Kommt hinterlegtes Zusatzmaterial beim Einfügen normalerweise mit? (ADR 0088) */
  kitDefaultOn: z.boolean(),
  tags: z.array(z.string()),
  isActive: z.boolean(),
  /** Ablängbarer Basisartikel (ADR 0040). */
  isCuttable: z.boolean(),
  /** VK je Grundeinheit für die Längen-Preisberechnung (null = nicht gepflegt). */
  cutUnitPrice: moneySchema.nullable(),
  /** Feste Abläng-/Rüstpauschale je Stück (null = keine). */
  cutSurcharge: moneySchema.nullable(),
  /** Materialisierte Variante → Basisartikel (null = normaler Artikel/Basisartikel). */
  parentArticleId: z.string().uuid().nullable(),
  /** Fixlänge der Variante in mm (null = keine Variante). */
  variantLengthMm: z.number().int().nullable(),
  /** Gedruckter Nummer-Anhang, der unter der Basisnummer erscheint (null = keiner). */
  numberSuffix: z.string().nullable(),
  /** Anzahl gekoppelter Sub-Artikel (Ablängungen) — nur am Hauptartikel > 0. */
  variantCount: z.number().int(),
  /** Artikelnummer des Hauptartikels (nur bei Sub-Artikeln gesetzt, sonst null). */
  parentArticleNumber: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ArticleResponse = z.infer<typeof articleResponseSchema>;
